<!--
  未过期验证码面板（独立板块，不是系统设置的一部分）。

  用途：用户报「没收到验证码 / 验证码不对」时，运维在这里直接核对。
  数据**直接读 Redis**（`SCAN verify:*`），不落 MySQL，也不写业务表 ——
  所以这里看到的就是真正参与校验的那份数据，没有「库里一份、缓存一份」的不一致。

  ⚠️ 权限模型（2026-10-26 收敛）：
     列表**不下发明文** —— 手机号/邮箱脱敏，验证码只保留位数（`_ _ _ _ _ _`）。
     要看明文必须点行上的「查看明文」，在弹窗里输入**完整**手机号/邮箱：
     排障时用户已经报过，粘过来即可。这样即使管理员会话被盗，也没法从列表
     一次性扫走所有人的验证码，必须逐个知道完整标识。
     两个动作分别记 VERIFY_CODE_VIEW（看列表）与 VERIFY_CODE_REVEAL（读明文）日志。

  ⚠️ 接口路径仍在 /settings 下（`GET /api/admin/v1/settings/verify-codes`）：
     历史原因，功能从「系统设置」页拆出来时没有改路由。改路由要同步后端
     AdminSettingController 与 @/api/admin 的 fetchVerifyCodes，别只改一边。
-->
<template>
  <div>
    <div class="adm-alert is-warning adm-code-alert">
      <i class="el-icon-warning-outline" />
      <span>
        验证码可用于以任意用户身份登录，列表只显示脱敏信息。
        查看明文需输入完整手机号/邮箱，并会单独记一条操作日志。
        数据来自系统缓存，只含未过期记录。
      </span>
    </div>

    <div class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />未过期验证码
          <span class="adm-card-title-extra">
            共 {{ codes.length }} 条{{ hasFilter ? '（已筛选）' : '' }}
          </span>
        </div>
        <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="load">
          刷新
        </el-button>
      </div>

      <div class="adm-filter">
        <!-- 关键字交给后端按原始值匹配：列表里的手机号是脱敏的，前端过滤搜不到完整号码 -->
        <el-input
          v-model="keyword"
          class="adm-code-w260"
          name="verifyCodeKeyword"
          autocomplete="off"
          prefix-icon="el-icon-search"
          placeholder="手机号 / 邮箱（回车搜索）"
          clearable
          @keyup.enter.native="load"
          @clear="load"
        />
        <el-select v-model="scene" class="adm-code-w160" placeholder="全部场景" clearable @change="load">
          <el-option
            v-for="item in sceneOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <span class="adm-filter-spacer" />
        <span class="adm-filter-note">剩余时间为拉取时刻的快照，点刷新可重新计算</span>
      </div>

      <div v-if="loading && !codes.length" class="adm-empty">
        <i class="el-icon-loading" />加载中…
      </div>
      <div v-else-if="!codes.length" class="adm-empty">
        <i class="el-icon-key" />{{ hasFilter ? '没有匹配的记录' : '当前没有未过期的验证码' }}
      </div>
      <el-table v-else v-loading="loading" :data="codes" border>
        <el-table-column label="场景" width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" type="info">{{ row.sceneLabel }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="手机号 / 邮箱" min-width="240">
          <template slot-scope="{ row }">
            <span class="adm-code">{{ row.identityMasked }}</span>
            <el-tag v-if="row.identityType" size="mini" class="adm-code-type">
              {{ identityTypeText(row.identityType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="验证码" width="130">
          <template slot-scope="{ row }">
            <span class="adm-code-value">{{ row.codeMasked }}</span>
          </template>
        </el-table-column>
        <el-table-column label="剩余有效期" width="130">
          <template slot-scope="{ row }">
            <span :class="{ 'adm-code-expiring': row.expiresInSeconds <= 60 }">
              {{ formatRemain(row.expiresInSeconds) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="110" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openReveal(row)">查看明文</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 查看明文：必须自己填完整标识，不复用列表里的脱敏值 -->
    <el-dialog title="查看验证码明文" :visible.sync="revealVisible" width="440px" append-to-body>
      <div class="adm-alert is-warning adm-code-alert">
        <i class="el-icon-warning" />
        <span>此操作会写入一条「查看验证码明文」操作日志。请仅在排障时使用。</span>
      </div>
      <el-form label-width="96px">
        <el-form-item label="场景">
          <span class="adm-code">{{ revealForm.sceneLabel || revealForm.scene }}</span>
        </el-form-item>
        <el-form-item label="身份类型">
          <span class="adm-code">{{ identityTypeText(revealForm.identityType) }}</span>
        </el-form-item>
        <el-form-item label="手机号/邮箱" required>
          <el-input
            v-model="revealForm.identity"
            :placeholder="revealPlaceholder"
            name="verifyCodeRevealIdentity"
            autocomplete="off"
            clearable
            @keyup.enter.native="submitReveal"
          />
        </el-form-item>
        <el-form-item v-if="revealed" label="验证码">
          <span class="adm-code-value adm-code-revealed">{{ revealed.code }}</span>
          <span class="adm-code-remain">剩余 {{ formatRemain(revealed.expiresInSeconds) }}</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="revealVisible = false">关闭</el-button>
        <el-button size="small" type="primary" :loading="revealLoading" @click="submitReveal">
          查看明文
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchVerifyCodes, revealVerifyCode, isOk } from '@/api/admin'

export default {
  name: 'VerifyCodePanel',
  data() {
    return {
      codes: [],
      loading: false,
      keyword: '',
      scene: '',
      // 场景下拉项：接口数据只含「当前有码的场景」，所以每次加载后合并进 options，
      // 避免筛到某个场景后下拉里只剩它自己、再也切不回别的。
      sceneCache: {},
      revealVisible: false,
      revealLoading: false,
      revealForm: { scene: '', sceneLabel: '', identityType: '', identity: '' },
      revealed: null
    }
  },
  computed: {
    hasFilter() {
      return !!this.keyword.trim() || !!this.scene
    },
    /** 场景下拉项从历史数据里归纳，不写死枚举 —— 后端加场景时前端无需改动 */
    sceneOptions() {
      return Object.keys(this.sceneCache).map(value => ({ value, label: this.sceneCache[value] }))
    },
    revealPlaceholder() {
      return this.revealForm.identityType === 'EMAIL' ? '完整邮箱，如 user@example.com' : '完整手机号，如 13800138000'
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      fetchVerifyCodes({ keyword: this.keyword.trim(), scene: this.scene })
        .then(res => {
          if (!isOk(res)) throw new Error(res && res.message ? res.message : '加载失败')
          // ⚠️ 后端把 Long 序列化成字符串，expiresInSeconds 也是串，这里统一转数字
          this.codes = (res.data || []).map(item => ({
            scene: item.scene,
            sceneLabel: item.sceneLabel || item.scene,
            identityType: item.identityType,
            identityMasked: item.identityMasked,
            codeMasked: item.codeMasked,
            expiresInSeconds: Number(item.expiresInSeconds)
          }))
          this.codes.forEach(item => {
            if (item.scene && !this.sceneCache[item.scene]) {
              this.$set(this.sceneCache, item.scene, item.sceneLabel)
            }
          })
        })
        .catch(err => {
          this.codes = []
          this.$message.error(err.message || '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    openReveal(row) {
      this.revealForm = {
        scene: row.scene,
        sceneLabel: row.sceneLabel,
        identityType: row.identityType,
        identity: ''
      }
      this.revealed = null
      this.revealVisible = true
    },
    submitReveal() {
      if (this.revealLoading) return
      const identity = this.revealForm.identity.trim()
      if (!identity) {
        this.$message.error('请填写完整手机号或邮箱')
        return
      }
      this.revealLoading = true
      revealVerifyCode({
        scene: this.revealForm.scene,
        identityType: this.revealForm.identityType,
        identity
      })
        .then(res => {
          if (!isOk(res)) throw new Error((res && res.message) || '读取失败')
          this.revealed = {
            code: res.data.code,
            expiresInSeconds: Number(res.data.expiresInSeconds)
          }
        })
        .catch(err => {
          this.revealed = null
          this.$message.error((err && err.message) || '读取失败')
        })
        .finally(() => {
          this.revealLoading = false
        })
    },
    identityTypeText(type) {
      if (type === 'EMAIL') return '邮箱'
      if (type === 'PHONE') return '手机号'
      return type || '-'
    },
    formatRemain(value) {
      const seconds = Number(value)
      if (!Number.isFinite(seconds)) return '-'
      if (seconds < 0) return '永久'
      const minutes = Math.floor(seconds / 60)
      const rest = seconds % 60
      return minutes > 0 ? minutes + ' 分 ' + rest + ' 秒' : rest + ' 秒'
    }
  }
}
</script>

<style scoped>
/* 宽度辅助类放在本组件内，与 UsersPanel 的 .adm-w260/.adm-w140 同思路 */
.adm-code-w260 {
  width: 260px;
}

.adm-code-w160 {
  width: 160px;
}

.adm-code-alert {
  margin-bottom: 16px;
}

.adm-code-type {
  margin-left: 8px;
}

.adm-code-value {
  font-family: Menlo, Consolas, monospace;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  color: var(--adm-primary);
}

/* 明文验证码：一次性展示，视觉上要和列表里的掩码区分开 */
.adm-code-revealed {
  font-size: 20px;
  letter-spacing: 3px;
}

.adm-code-remain {
  margin-left: 12px;
  font-size: 12px;
  color: var(--adm-text-secondary, #909399);
}

/* 剩余不足 1 分钟：提醒运维这条马上就没用了 */
.adm-code-expiring {
  color: var(--adm-danger);
  font-weight: 600;
}
</style>
